import { useState } from 'react';

/**
 * useDialogState can be used to simplify interacting with dialogs with a controlled open/closed state
 * The dialog should accept an isOpen prop and only be rendered when that value is true (the material UI dialog already
 * works like this).
 * If the dialog needs input from its parent (where the dialog component is rendered), you can
 * hold those input in the data state.
 *
 * Usecase: a dialog that can be opened from different sources and wants to do something different based on what
 * source opened it.
 * Examples:
 *    - opening the dialog from a list of users, each line having its own button to open the dialog.
 *      we can keep the user's id in data, and give data to the dialog as a prop and let the dialog fetch the user
 *      details when it opens.
 *    - want to display a different title for the dialog based on what opened it
 */
interface Props {
  isOpen?: boolean;
}

type DialogState<DataType> = {
  isOpen: boolean;
  data: DataType;
};

export const useDialogState = <DataType>(props: Props = {}) => {
  const [state, setState] = useState<DialogState<DataType>>({
    isOpen: props.isOpen ?? false,
    data: undefined,
  });

  const open = (dialogData?: DataType) =>
    setState({
      isOpen: true,
      data: dialogData,
    });

  const close = (clearData: boolean = true) =>
    setState((currentState) => ({
      isOpen: false,
      data: clearData ? undefined : currentState.data,
    }));

  const setIsOpen = (value: boolean) => setState((state) => ({ ...state, isOpen: value }));
  const setData = (data: DataType) => setState((state) => ({ ...state, data }));
  const toggle = () => setState((state) => ({ ...state, isOpen: !state.isOpen }));

  return {
    isOpen: state.isOpen,
    setIsOpen,
    data: state.data,
    setData,
    open,
    close,
    toggle,
  };
};
